import {
  FileText,
  Cpu,
  ShieldCheck,
  Radar,
  PackageCheck,
  ArrowRight,
} from "lucide-react";

const NODES = [
  {
    icon: FileText,
    title: "Purchase Order",
    sub: "Order intake",
    id: "PO",
  },
  {
    icon: Cpu,
    title: "FreightIQ™",
    sub: "Booking coordination",
    id: "FQ",
    accent: true,
  },
  {
    icon: ShieldCheck,
    title: "Inspection™",
    sub: "Quality at origin",
    id: "IN",
  },
  {
    icon: Radar,
    title: "Live Shipment Tracking™",
    sub: "Real-time milestones",
    id: "TR",
  },
  {
    icon: PackageCheck,
    title: "Delivered",
    sub: "Workspace archive",
    id: "DL",
  },
];

export default function WorkspaceIntegration() {
  return (
    <section
      id="workspace"
      data-testid="workspace-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.18em] uppercase font-bold text-[#3B82F6]">
            Integrated Workspace
          </div>
          <h2 className="mt-3 font-display font-extrabold text-4xl md:text-5xl tracking-[-0.02em] text-[#0B1F3A]">
            One connected workflow.
            <br />
            <span className="text-[#0B1F3A]/55">Order to delivery.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            FreightIQ™ doesn&apos;t live in isolation. It&apos;s a native module inside
            the DeMaxtore Workspace — connected to every shipment touchpoint.
          </p>
        </div>

        <div
          id="tracking"
          className="relative mt-16 rounded-3xl bg-[#0B1F3A] text-white p-8 md:p-12 lg:p-16 overflow-hidden dmx-sweep"
        >
          {/* Decorative grid */}
          <div
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            }}
          />

          <div className="relative">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#3B82F6]">
                  DeMaxtore Workspace
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl font-bold tracking-tight">
                  Purchase Order → Delivered
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono text-white/50">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                ALL MODULES CONNECTED
              </div>
            </div>

            {/* Desktop horizontal flow */}
            <div className="mt-12 hidden lg:flex items-stretch w-full min-w-0">
              {NODES.map((n, idx) => {
                const Icon = n.icon;
                return (
                  <div key={n.id} className="contents">
                    {idx > 0 && (
                      <div className="flex flex-1 items-center min-w-[20px] px-1">
                        <div className="relative flex w-full items-center">
                          <div className="h-px w-full bg-gradient-to-r from-[#3B82F6]/30 via-[#3B82F6] to-[#3B82F6]/30" />
                          <ArrowRight className="absolute right-0 h-3.5 w-3.5 shrink-0 text-[#3B82F6]" />
                        </div>
                      </div>
                    )}
                    <div
                      data-testid={`workspace-node-${idx}`}
                      className={`shrink-0 rounded-2xl border p-5 w-[170px] xl:w-[190px] 2xl:w-[200px] ${
                        n.accent
                          ? "bg-[#3B82F6] border-[#3B82F6] shadow-[0_20px_40px_-15px_rgba(59,130,246,0.6)]"
                          : "bg-white/[0.04] border-white/10 backdrop-blur-sm"
                      }`}
                    >
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                          n.accent
                            ? "bg-white/15 text-white"
                            : "bg-[#3B82F6]/15 text-[#3B82F6]"
                        }`}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                      </div>
                      <div className="mt-4 font-display text-base font-bold tracking-tight text-white">
                        {n.title}
                      </div>
                      <div
                        className={`mt-1 text-xs ${
                          n.accent ? "text-white/80" : "text-white/50"
                        }`}
                      >
                        {n.sub}
                      </div>
                      <div className="mt-3 font-mono text-[10px] text-white/40">
                        ID · {n.id}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile vertical flow */}
            <div className="mt-10 lg:hidden space-y-3">
              {NODES.map((n, idx) => {
                const Icon = n.icon;
                return (
                  <div key={n.id}>
                    <div
                      className={`rounded-2xl border p-5 flex items-center gap-4 ${
                        n.accent
                          ? "bg-[#3B82F6] border-[#3B82F6]"
                          : "bg-white/[0.04] border-white/10"
                      }`}
                    >
                      <div
                        className={`flex items-center justify-center h-10 w-10 rounded-lg shrink-0 ${
                          n.accent
                            ? "bg-white/15 text-white"
                            : "bg-[#3B82F6]/15 text-[#3B82F6]"
                        }`}
                      >
                        <Icon className="h-4.5 w-4.5 h-5 w-5" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold tracking-tight">
                          {n.title}
                        </div>
                        <div className="text-xs text-white/60">{n.sub}</div>
                      </div>
                      <span className="ml-auto font-mono text-[10px] text-white/40">
                        {n.id}
                      </span>
                    </div>
                    {idx < NODES.length - 1 && (
                      <div className="flex justify-center py-1">
                        <div className="h-5 w-px bg-gradient-to-b from-[#3B82F6]/0 via-[#3B82F6] to-[#3B82F6]/0" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Internal links */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4" id="inspection">
          {[
            { l: "FreightIQ™", h: "#freightiq", d: "Booking & coordination" },
            { l: "Live Shipment Tracking™", h: "#tracking", d: "Real-time milestones" },
            { l: "Inspection™", h: "#inspection", d: "Quality at origin" },
            { l: "Workspace", h: "#workspace", d: "One connected OS" },
          ].map((i) => (
            <a
              key={i.l}
              href={i.h}
              data-testid={`internal-link-${i.l.toLowerCase().replace(/[^a-z]/g, "")}`}
              className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0B1F3A]/30 transition-colors"
            >
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">
                Explore
              </div>
              <div className="mt-2 font-display font-bold text-[#0B1F3A] flex items-center gap-1">
                {i.l}
                <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </div>
              <div className="mt-1 text-xs text-slate-500">{i.d}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
